import { useEffect, useState } from 'react';
import { AlertService } from '../../../../services/alert.service';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CompaniesService } from '../../../../services/companies.service';
import { AccountsService } from '../../../../services/accounts.service';
import { useSession } from '../../../../hooks/use-session.hook';
import { PROFILES } from '../../../../constants';
import Input from '../../../../components/forms/input.component';
import Icon from '../../../../components/forms/icon.component';
import Select from 'react-select';
import Title from '../../../../components/forms/titles.component';
import { TemplatesService } from '../../../../services/templates.service';

function Stepper1({ onValidationChange }: { onValidationChange: (isValid: boolean) => void }) {
  const { t } = useTranslation();
  const { session, selectedAccount } = useSession();
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [optionsAccounts, setOptionsAccounts] = useState<any[]>([]);
  const [templatesAvailable, setTemplatesAvailable] = useState<{ [key: string]: boolean }>({});
  const [loading, setLoading] = useState(false);

  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors }
  } = useFormContext();

  const {
    typeCampaign,
    account,
    companies: companiesSelected,
    name,
    account: accountSelected,
    channelsAvailable
  } = watch();

  const getChannels = async () => {
    try {
      const response = await AccountsService.getChannelsForAccount(account);
      const channels = response?.channels || {};
      const available = Object.entries(channels)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .filter(([_, items]) => Array.isArray(items) && items.length > 0)
        .map(([key]) => key);

      setValue('channelsAvailable', available);
      setValue('accountCahnnels', response?.channels);
    } catch (errorCode: any) {
      console.error('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const checkTemplatesAvailability = async () => {
    if (!account || !channelsAvailable?.length) {
      setTemplatesAvailable({});
      return;
    }

    const templatesStatus: { [key: string]: boolean } = {};

    try {
      const channelsResponse = await AccountsService.getChannelsForAccount(account);
      const channels = channelsResponse?.channels || {};
      for (const channel of channelsAvailable) {
        if (channel === 'sms') {
          templatesStatus[channel] = true;
          continue;
        }

        let hasTemplates = false;

        switch (channel) {
          case 'whatsapp': {
            const whatsappAccounts = channels?.whatsapp?.filter(
              (item: any) => item.status === true && item.config?.status === true
            );

            if (whatsappAccounts?.length > 0) {
              const accountId = whatsappAccounts[0].config._id;
              const templates = await TemplatesService.getTemplatesAccountWa(accountId);
              const approvedTemplates = Object.values(templates || {})
                .flat()
                .filter((template: any) => template.status?.toUpperCase() === 'APPROVED');
              hasTemplates = approvedTemplates.length > 0;
            }
            break;
          }

          case 'rcs_basic':
          case 'rcs_single':
          case 'rcs_conversational': {
            const templates = await TemplatesService.getTemplatesAccountRCS(account);
            const allTemplates = Object.values(templates || {}).flat() as any[];

            let filteredTemplates = [];
            if (channel === 'rcs_basic') {
              filteredTemplates = allTemplates.filter(
                (t) => t.type?.toLowerCase() === 'basic' && t.status === true
              );
            } else if (channel === 'rcs_single') {
              filteredTemplates = allTemplates.filter(
                (t) => t.type?.toLowerCase() === 'single' && t.status === true
              );
            } else if (channel === 'rcs_conversational') {
              filteredTemplates = allTemplates.filter(
                (t) => t.type?.toLowerCase() === 'conversational' && t.status === true
              );
            }

            hasTemplates = filteredTemplates.length > 0;
            break;
          }

          case 'email': {
            const templates = await TemplatesService.getTemplatesAccountMailing(account);
            const allTemplates = Object.values(templates || {}).flat() as any[];
            const activeTemplates = allTemplates.filter(
              (template: any) => template.status === true
            );
            hasTemplates = activeTemplates.length > 0;
            break;
          }

          default:
            hasTemplates = true;
        }

        templatesStatus[channel] = hasTemplates;
      }

      setTemplatesAvailable(templatesStatus);
    } catch (error) {
      console.error('Error checking templates availability:', error);
      const errorStatus: { [key: string]: boolean } = {};
      channelsAvailable.forEach((channel: string) => {
        errorStatus[channel] = channel === 'sms';
      });
      setTemplatesAvailable(errorStatus);
    }
  };

  const getReadableChannelName = (channel: string): string => {
    switch (channel) {
      case 'sms':
        return 'SMS';
      case 'whatsapp':
        return 'WhatsApp';
      case 'rcs_basic':
        return 'RCS Básico';
      case 'rcs_single':
        return 'RCS Simple';
      case 'rcs_conversational':
        return 'RCS Conversational';
      case 'email':
        return 'Email';
      default:
        return channel;
    }
  };

  const getValidChannels = (channels: string[]): string[] => {
    const transformed = channels.map((ch) =>
      ['rcs_basic', 'rcs_single', 'rcs_conversational'].includes(ch) ? 'rcs_grouped' : ch
    );
    const unique = Array.from(new Set(transformed));
    return unique.filter((ch) => ch !== '0');
  };

  const validChannels = getValidChannels(channelsAvailable);

  const shouldShowEmptyMessage =
    ((typeCampaign === 'DEFAULT' || typeCampaign === 'FLOW') && channelsAvailable.length === 0) ||
    (typeCampaign === 'FALLBACK' && validChannels.length < 2) ||
    (typeCampaign === 'FLOW' &&
      !channelsAvailable.some((ch: string) => ch === 'whatsapp' || ch === 'rcs_conversational'));

  const channelsWithoutTemplates =
    channelsAvailable?.filter(
      (channel: string) => channel !== 'sms' && templatesAvailable[channel] === false
    ) || [];
  const channelsWithTemplates =
    channelsAvailable?.filter((channel: string) => templatesAvailable[channel] === true) || [];

  const shouldShowTemplatesMessage = channelsWithoutTemplates.length > 0 && account;
  const canContinueWithTemplates = channelsWithTemplates.length > 0;

  const emptyChannels = (() => {
    if (!shouldShowEmptyMessage) return '';

    // Para campañas DEFAULT o FLOW sin canales
    if (typeCampaign === 'DEFAULT' || (typeCampaign === 'FLOW' && channelsAvailable.length === 0)) {
      return session.profile === PROFILES.ADMIN
        ? 'La cuenta que estás utilizando no tiene ningún canal de envío asignado, por lo que no puedes continuar. Por favor, contacta a tu administrador.'
        : 'La cuenta seleccionada no tiene ningún canal de envío asignado, por lo que no puedes continuar. Por favor, contacta a soporte.';
    }

    // Para campaña FLOW sin canales whatsapp ni rcs_conversational
    if (
      typeCampaign === 'FLOW' &&
      !channelsAvailable.some((ch: string) => ch === 'whatsapp' || ch === 'rcs_conversational')
    ) {
      let readableName = '';

      if (channelsAvailable.includes('sms')) {
        readableName = 'SMS';
      } else if (channelsAvailable.includes('whatsapp')) {
        readableName = 'WhatsApp';
      } else if (
        channelsAvailable.includes('rcs_single') ||
        channelsAvailable.includes('rcs_basic')
      ) {
        const found = channelsAvailable.find((ch: string) =>
          ['rcs_basic', 'rcs_single'].includes(ch)
        );
        readableName = found ? getReadableChannelName(found) : 'RCS';
      } else {
        readableName = 'canal asignado';
      }

      return (
        <>
          <p>Tienes diferentes tipos de canales:</p>
          <ul className="text-info">
            <li>
              <strong>{readableName}</strong>
            </li>
          </ul>
          <p>
            Para realizar una campaña de Flujos de mensajes, necesitas al menos un canal de WhatsApp
            o RCS Conversacional.
          </p>
        </>
      );
    }

    // Para campaña FALLBACK con menos de 2 canales válidos
    if (typeCampaign === 'FALLBACK') {
      if (validChannels.length === 0) {
        return session.profile === PROFILES.ADMIN
          ? 'La cuenta que estás utilizando no tiene ningún canal de envío asignado, por lo que no puedes continuar. Por favor, contacta a tu administrador.'
          : 'La cuenta seleccionada no tiene ningún canal de envío asignado, por lo que no puedes continuar. Por favor, contacta a soporte.';
      }

      if (validChannels.length === 1) {
        const [onlyChannel] = validChannels;

        let readableName = '';
        if (onlyChannel === 'sms') readableName = 'SMS';
        else if (onlyChannel === 'whatsapp') readableName = 'WhatsApp';
        else if (onlyChannel === 'rcs_grouped') {
          const found = channelsAvailable.find((ch: any) =>
            ['rcs_basic', 'rcs_single', 'rcs_conversational'].includes(ch)
          );
          readableName = getReadableChannelName(found);
        } else {
          readableName = onlyChannel;
        }

        return (
          <>
            <p>Solo tienes un tipo de canal asignado: </p>
            <ul className="text-info">
              <li>
                <strong>{readableName}</strong>
              </li>
            </ul>
            <p>
              Para realizar una campaña Fallback, necesitas al menos dos tipos diferentes de
              canales.
            </p>
          </>
        );
      }
    }

    return '';
  })();

  const getChannelIcon = (channel: string): string => {
    switch (channel) {
      case 'sms':
        return 'ki-message-notif';
      case 'whatsapp':
        return 'ki-whatsapp';
      case 'rcs basic':
      case 'rcs single':
      case 'rcs conversational':
        return 'ki-messages';
      case 'email':
        return 'ki-sms';
      default:
        return 'ki-notification';
    }
  };

  const emptyTemplatesMessage = (() => {
    if (!shouldShowTemplatesMessage) return '';

    const channelNamesWithoutTemplates = channelsWithoutTemplates.map(getReadableChannelName);
    const channelNamesWithTemplates = channelsWithTemplates.map(getReadableChannelName);
    const totalChannels = channelsAvailable?.length || 0;
    const isPlural = channelNamesWithoutTemplates.length > 1;
    const hasChannelsWithTemplates = channelsWithTemplates.length > 0;

    const handleRedirectToTemplates = () => {
      window.location.href = '/templates-panel';
    };

    return (
      <>
        <p>
          Tienes <strong>{totalChannels}</strong>{' '}
          {totalChannels === 1 ? 'canal activo' : 'canales activos'}, pero{' '}
          <strong>{channelsWithoutTemplates.length}</strong> de{' '}
          {totalChannels === 1 ? 'este' : 'estos'} no {isPlural ? 'tienen' : 'tiene'} plantillas
          registradas.
        </p>
        <ul className="text-danger mb-4 list-unstyled">
          {hasChannelsWithTemplates && (
            <>
              <p className="mb-4">
                <strong className="text-primary">Con plantillas:</strong>
              </p>
              <ul className="text-primary mb-4 list-unstyled">
                {channelNamesWithTemplates.map((name: string, index: number) => (
                  <li key={index} className="d-flex align-items-center mb-2">
                    <Icon
                      icon={getChannelIcon(name.toLowerCase())}
                      size="fs-3"
                      type="primary"
                      className="me-2"
                    />
                    <strong>{name}</strong>
                  </li>
                ))}
              </ul>
            </>
          )}
          {channelNamesWithoutTemplates.map((name: string, index: number) => (
            <>
              <p className="mb-4">
                <strong className="text-danger">Sin plantillas:</strong>
              </p>
              <li key={index} className="d-flex align-items-center mb-2">
                <Icon
                  icon={getChannelIcon(name.toLowerCase())}
                  size="fs-3"
                  type="danger"
                  className="me-2"
                />
                <strong>{name}</strong>
              </li>
            </>
          ))}
        </ul>

        <p>
          Para poder enviar {isPlural ? 'campañas por estos canales' : 'campañas por este canal'},
          que aún no {isPlural ? 'tienen' : 'tiene'} plantillas registradas, puedes
          <span
            className="text-primary fw-bold cursor-pointer text-decoration-underline ms-2"
            onClick={handleRedirectToTemplates}
          >
            crearla aquí
          </span>
        </p>
      </>
    );
  })();

  const getCompanies = async () => {
    try {
      const data = await CompaniesService.getCompanies();
      setCompanies(
        data.map((item: any) => ({
          value: item._id,
          label: item.name
        }))
      );
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
      setCompanies([]);
    }
  };

  const getAccount = async () => {
    if (selectedCompany)
      try {
        const data = await AccountsService.getAccountsForCompany(selectedCompany);
        setOptionsAccounts(
          data
            .filter((item: any) => item.status === true)
            .map((item: any) => ({
              value: item._id,
              label: item.name
            }))
        );
      } catch (errorCode: any) {
        console.log('Error:', errorCode);
        AlertService.toastError(errorCode.error || errorCode);
        setOptionsAccounts([]);
      }
  };

  const stepper1Valid =
    !!companiesSelected && !!name && !!accountSelected && canContinueWithTemplates;

  useEffect(() => {
    getCompanies();
  }, []);

  useEffect(() => {
    if (session.profile === PROFILES.ADMIN) {
      setValue('companies', session._id);
      setValue('account', selectedAccount.account_id);
    }
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      getAccount();
    }
  }, [selectedCompany]);

  useEffect(() => {
    onValidationChange(stepper1Valid);
  }, [
    accountSelected,
    name,
    companiesSelected,
    canContinueWithTemplates,
    stepper1Valid,
    onValidationChange
  ]);

  useEffect(() => {
    const fetchChannels = async () => {
      if (account) {
        await getChannels();
        setValue('channel', '');
      }
    };

    fetchChannels();
  }, [account]);

  useEffect(() => {
    if (account && channelsAvailable?.length > 0) {
      checkTemplatesAvailability();
    } else {
      setTemplatesAvailable({});
    }
  }, [account, channelsAvailable]);

  useEffect(() => {
    const fetchChannelsAndTemplates = async () => {
      if (!account) return;

      setLoading(true);

      try {
        await getChannels();

        if (channelsAvailable?.length > 0) {
          await checkTemplatesAvailability();
        } else {
          setTemplatesAvailable({});
        }
      } catch (error) {
        console.error('Error al obtener canales o templates:', error);
        setTemplatesAvailable({});
      } finally {
        setLoading(false);
      }
    };

    fetchChannelsAndTemplates();
  }, [account]);

  return (
    <>
      <div className="text-left mb-3">
        <Title
          icon="ki-message-edit"
          title={'Nombre y Empresa'}
          subtitle={'Ingresa los datos necesarios.'}
          type="info"
        />
      </div>
      <div className="fv-row mb-10">
        <div className="d-flex align-items-center mb-3">
          <span className="symbol me-e6 symbol-label"></span>
          <div className="d-flex flex-column">
            <span className="fw-bolder fs-3 required">Nombre</span>
            <span className="fs-6">Escribe un nombre para tu campaña.</span>
          </div>
        </div>
        <Input
          placeholder={'Ingresa un nombre'}
          errors={errors?.name}
          {...register('name', {
            required: true,
            setValueAs: (value) => value.trim()
          })}
        />
      </div>
      {session.profile === PROFILES.ROOT && (
        <>
          <div className="separator separator-dashed text-dark mb-10"></div>
          <div className="d-flex align-items-center mb-10">
            <span className="symbol me-e6 symbol-label"></span>
            <div className="d-flex flex-column">
              <span className="fw-bolder fs-3 required">Empresa y cuentas</span>
              <span className="fs-6">
                Elige una empresa y una cuenta disponible. Esa cuenta será la que se utilice para
                enviar tu campaña.
              </span>
            </div>
          </div>
          <div className="row">
            <div className={`fv-row col-md-${watch('companies') ? '6' : '12'} mb-5`}>
              <label className="form-label" htmlFor="companies">
                Empresa
              </label>
              <Controller
                control={control}
                rules={{ required: true }}
                name={'companies'}
                render={({ field: { onChange, value } }) => (
                  <Select
                    id="companies"
                    options={companies}
                    noOptionsMessage={({ inputValue }) =>
                      inputValue ? t('general.no-options', { inputValue }) : t('general.no-options')
                    }
                    value={companies.filter((option: any) => value?.includes(option.value)) || []}
                    onChange={(selectedOption) => {
                      const selectedValue = selectedOption?.value || '';
                      setSelectedCompany(selectedOption?.value || '');
                      onChange(selectedValue);
                      setValue('account', '');
                    }}
                    placeholder={'Selecciona una empresa'}
                    className="form-select-custom"
                    classNamePrefix="form-select-custom"
                  />
                )}
              />
            </div>
            {companiesSelected && (
              <div className="col-md-6 mb-5">
                <label className="form-label" htmlFor="account">
                  Cuenta
                </label>
                <Controller
                  control={control}
                  rules={{ required: true }}
                  name={'account'}
                  render={({ field: { onChange, value } }) => (
                    <Select
                      id="account"
                      options={optionsAccounts}
                      noOptionsMessage={({ inputValue }) =>
                        inputValue
                          ? t('general.no-options', { inputValue })
                          : t('general.no-options')
                      }
                      onChange={(
                        selectedOption: {
                          value: string;
                          label: string;
                        } | null
                      ) => {
                        onChange(selectedOption ? selectedOption.value : null);
                      }}
                      value={optionsAccounts.find((option: any) => option.value === value) || ''}
                      placeholder={'Selecciona una cuenta'}
                      className="form-select-custom"
                      classNamePrefix="form-select-custom"
                    />
                  )}
                />
              </div>
            )}
          </div>
        </>
      )}
      {loading ? (
        <div className="loader"></div>
      ) : (
        <>
          {(((typeCampaign === 'DEFAULT' || typeCampaign === 'FLOW') &&
            channelsAvailable.length === 0 &&
            account) ||
            (typeCampaign === 'FALLBACK' && validChannels.length < 2 && account) ||
            (typeCampaign === 'FLOW' &&
              channelsAvailable.length >= 1 &&
              !channelsAvailable.some(
                (ch: string) => ch === 'whatsapp' || ch === 'rcs_conversational'
              ))) && (
            <div className="d-flex align-items-center justify-content-center gap-10 flex-column flex-md-row w-100 p-5 animate-fadeInUp animate-delay-1">
              <div className="text-start">
                <h5 className="mb-3 text-danger fw-bolder fs-1">¡ Atención !</h5>
                <div className="fs-5 fw-bolder">{emptyChannels}</div>
              </div>
              <div className="text-center">
                <img
                  src="/assets/media/illustrations/undraw/no-channels.svg"
                  alt="No se encontraron canales"
                  style={{ maxWidth: '280px' }}
                />
              </div>
            </div>
          )}
          {shouldShowTemplatesMessage && (
            <div className="d-flex align-items-center justify-content-center gap-10 flex-column flex-md-row w-100 p-5 animate-fadeInUp animate-delay-1">
              <div className="text-start">
                <h5 className="mb-3 text-danger fw-bolder fs-1">¡ Atención !</h5>
                <div className="fs-5 fw-bolder">{emptyTemplatesMessage}</div>
              </div>
              <div className="text-center">
                <img
                  src="/assets/media/illustrations/undraw/no-templates.svg"
                  alt="No se encontraron templates"
                  style={{ maxWidth: '280px' }}
                />
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

export default Stepper1;
